import { formatEwcDisplayCode } from "../../../components/Forms/EwcCodeInput";
import { WasteReturnRegFieldKeys } from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { SortOrder } from "../../../components/UI/Declarations";
import { formatDrMembershipLabels } from "./DrRegDataComparisonRow";
import { RegDataChangeTypes } from "./regDataAlignmentDisplay";
import { buildSystemDisplayProps, type SystemDisplayProps } from "./reconciliationRowVisualState";
import type { ReconciliationGridCardModel, ReconciliationGridRow } from "./reconciliationGridTypes";
import { drFlagsFromParsed } from "./DrRegDataComparisonRow";

export const PreviewModalActions = {
    Add: "Add",
    Update: "Update",
    Delete: "Delete",
} as const;

export type PreviewModalAction = (typeof PreviewModalActions)[keyof typeof PreviewModalActions];

export interface PreviewModalRow {
    changeKey: string;
    values: string;
    action: PreviewModalAction;
}

export function mapStagedChangeToPreviewAction(changeType: string): PreviewModalAction {
    switch (changeType) {
        case RegDataChangeTypes.AddNewFromTemplate:
            return PreviewModalActions.Add;
        case RegDataChangeTypes.SoftDeleteExisting:
            return PreviewModalActions.Delete;
        default:
            return PreviewModalActions.Update;
    }
}

export function formatPostApplyPreviewValue(row: ReconciliationGridRow): string {
    const displayProps = buildSystemDisplayProps(row);
    return formatPreviewValueForField(row, displayProps);
}

function formatPreviewValueForField(row: ReconciliationGridRow, displayProps: SystemDisplayProps): string {
    if (row.fieldKey === WasteReturnRegFieldKeys.Ewc) {
        const code = formatEwcDisplayCode(displayProps.displayCode ?? row.normalizedKey);
        const isHazardous =
            displayProps.isHazardous ??
            (row.systemSide.option?.structured?.isHazardous === true);
        return `${code} | Hazardous? ${isHazardous ? "Yes" : "No"}`;
    }

    if (row.fieldKey === WasteReturnRegFieldKeys.EaDr) {
        const code = (displayProps.displayCode ?? row.normalizedKey).trim() || "—";
        const flags =
            displayProps.isForReceipt != null || displayProps.isForRemoval != null
                ? {
                      isForReceipt: displayProps.isForReceipt ?? false,
                      isForRemoval: displayProps.isForRemoval ?? false,
                  }
                : drFlagsFromParsed(row.systemSide.option?.structured);
        const labels = formatDrMembershipLabels(flags.isForReceipt, flags.isForRemoval);
        return labels.length > 0 ? `${code} | ${labels.join(" | ")}` : code;
    }

    if (row.fieldKey === WasteReturnRegFieldKeys.Geography) {
        const code = (displayProps.displayCode ?? "").trim();
        const type = (displayProps.displayType ?? "").trim();
        if (code && type) return `${code} | ${type}`;
        return code || type || displayProps.rawDisplayValue?.trim() || "—";
    }

    if (row.fieldKey === WasteReturnRegFieldKeys.FacilityOperation) {
        return (displayProps.displayCode ?? "").trim() || "—";
    }

    const code = (displayProps.displayCode ?? "").trim();
    if (code) return code;

    return displayProps.rawDisplayValue?.trim() || row.systemSide.displayValue?.trim() || "—";
}

export function buildPreviewModalRowsForCard(card: ReconciliationGridCardModel): PreviewModalRow[] {
    return card.gridRows
        .filter((row) => row.isStaged && row.stagedChange && row.stagedChangeKey)
        .map((row) => ({
            changeKey: row.stagedChangeKey!,
            values: formatPostApplyPreviewValue(row),
            action: mapStagedChangeToPreviewAction(row.stagedChange!.changeType),
        }));
}

export function buildPreviewModalSections(
    cardModels: ReconciliationGridCardModel[]
): { fieldKey: string; label: string; rows: PreviewModalRow[] }[] {
    return cardModels
        .map((card) => ({
            fieldKey: card.fieldKey,
            label: card.label,
            rows: buildPreviewModalRowsForCard(card),
        }))
        .filter((section) => section.rows.length > 0);
}

export function sortPreviewModalRows(
    rows: PreviewModalRow[],
    sortBy: string,
    sortOrder: SortOrder
): PreviewModalRow[] {
    const direction = sortOrder === SortOrder.ascending ? 1 : -1;
    return [...rows].sort((left, right) => {
        const leftValue = sortBy === "action" ? left.action : left.values;
        const rightValue = sortBy === "action" ? right.action : right.values;
        return leftValue.localeCompare(rightValue, undefined, { sensitivity: "base" }) * direction;
    });
}
